import type { Area, Label } from './types';
import { liveAreas, liveLabels } from './types';

/**
 * THE AREA / EPIC IDENTITY PALETTE — the colours a categorical object (an area,
 * and the epic tints derived from the same set) can wear, in the order they are
 * offered. The single source for every place that gives an area a colour: the
 * Settings editor's swatches and the inline "+ Create area" on a card. Kept here
 * (not in a component) so the two can never drift apart.
 *
 * SIXTEEN, DELIBERATELY — and no higher. Vinnie, 17 October 2026: "we need more
 * colors for each category… epics are an object along with area." Eight could not
 * tell a real IT estate's areas apart, so this doubles the usable set. It stops
 * at sixteen because categorical-colour perception, at a 10px dot or a 3px rail,
 * tops out around there; past it the board reads as a rainbow and areas stop
 * being tellable at a glance — the opposite of the point. Twelve core colours
 * cover a department with headroom; four muted ones extend to sixteen for the
 * largest estates without the grid ever becoming a rainbow.
 *
 * CLEAR OF THE STATUS INKS. The old palette reused done-green (#35a875),
 * in-progress-amber (#c8821c) and critical-red (#f06d6d) — so a red rail read as
 * "blocked", a green dot as "done". Those three are gone from here: an area is
 * never mistaken for a status signal. Areas that still carry a retired hex keep
 * it until they are re-picked — nothing recolours underneath anyone.
 *
 * ONE HEX, BOTH THEMES. An area colour is a single stored hex, drawn unchanged on
 * the near-black and the white ground alike, so every value is mid-luminance and
 * moderately saturated — it has to read on both. The rgb triplets below are
 * mirrored into --rgb-a1..a16 in styles.css (BOTH theme blocks, same values) for
 * the epic tints; test-contrast.ts proves the two spellings agree and that both
 * themes match.
 */
export interface AreaColour {
  /** The stored value — what lands on the row and the rail. */
  hex: string;
  /** A plain name, shown in the swatch tooltip so the picker is not sixteen bare squares. */
  name: string;
}

/**
 * The split between the twelve core colours and the four muted ones, so the
 * colour picker can group them ("Core" / "Muted") rather than show one long run.
 */
export const AREA_CORE_COUNT = 12;

export const AREA_COLOURS: AreaColour[] = [
  // The twelve core — a full spread, each clear of its neighbours and of the RAG set.
  { hex: '#5b8def', name: 'Azure' },
  { hex: '#7a7ee6', name: 'Indigo' },
  { hex: '#9478e8', name: 'Violet' },
  { hex: '#bd6fd0', name: 'Orchid' },
  { hex: '#e072a0', name: 'Rose' },
  { hex: '#4bc5ce', name: 'Teal' },
  { hex: '#5f8fb0', name: 'Steel' },
  { hex: '#3fb296', name: 'Jade' },
  { hex: '#97a855', name: 'Olive' },
  { hex: '#c9a94e', name: 'Gold' },
  { hex: '#e0925c', name: 'Clay' },
  { hex: '#8b8b96', name: 'Stone' },
  // The four muted — lower chroma, so reaching sixteen never tips into a rainbow.
  { hex: '#45a9d6', name: 'Cyan' },
  { hex: '#9f5e95', name: 'Plum' },
  { hex: '#6f9668', name: 'Fern' },
  { hex: '#a8927d', name: 'Taupe' },
];

/**
 * The colours as plain hexes, in offer order — the shape every caller already
 * expects (the swatches, the starter set, nextAreaColour). Derived from
 * AREA_COLOURS so the names and the values can never drift.
 */
export const AREA_PALETTE = AREA_COLOURS.map((c) => c.hex);

/** The human name for a stored hex, for a swatch's tooltip. Undefined for a retired/legacy hex. */
export function areaColourName(hex: string): string | undefined {
  return AREA_COLOURS.find((c) => c.hex.toLowerCase() === (hex ?? '').toLowerCase())?.name;
}

/**
 * The colour to give a NEW area: the palette colour the fewest live areas already
 * use, so colours spread across the board rather than piling onto the first
 * swatch. Ties fall to the earlier palette colour, so the first sixteen areas
 * march straight down the palette, one each, before any colour repeats.
 */
export function nextAreaColour(areas: Area[]): string {
  const live = liveAreas(areas);
  let best = AREA_PALETTE[0];
  let bestN = Infinity;
  for (const c of AREA_PALETTE) {
    const n = live.filter((a) => (a.colour ?? '').toLowerCase() === c.toLowerCase()).length;
    if (n < bestN) {
      bestN = n;
      best = c;
    }
  }
  return best;
}

/**
 * The colour to give a NEW label — the same spread-by-least-used rule as
 * nextAreaColour, over the SAME identity palette, so a label created inline on a
 * card or in Settings is coloured automatically rather than landing as a plain
 * neutral chip (Vinnie, 5 November 2026: "can the labels auto color when they're
 * created"). Only live labels that actually carry a palette colour count toward
 * the spread, so the legacy free-typed labels that have no colour yet never pin a
 * swatch as "used". A colour is still clearable in Settings for the rare label a
 * board wants to keep plain.
 */
export function nextLabelColour(labels: Label[]): string {
  const live = liveLabels(labels);
  let best = AREA_PALETTE[0];
  let bestN = Infinity;
  for (const c of AREA_PALETTE) {
    const n = live.filter((l) => (l.colour ?? '').toLowerCase() === c.toLowerCase()).length;
    if (n < bestN) {
      bestN = n;
      best = c;
    }
  }
  return best;
}
